import { WIDGET_FONT_IDS, ensureWidgetFontStylesheet, toWidgetFontId, widgetFontFamily, widgetFontStack, type WidgetFontId } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Select, cn, cssVars } from "@sagot/ui";
import { useEffect } from "react";

const K = "deployKnowledge.widgetStudio.appearance";

/**
 * Loads the chosen Google family into the console document (same one-`<link>` rule as the widget
 * loader) so the picker sample and the live preview render in the typeface visitors will see.
 */
export function useWidgetFontStylesheet(font: WidgetFontId): void {
  useEffect(() => {
    ensureWidgetFontStylesheet(document, font);
  }, [font]);
}

interface FontPickerProps {
  value: WidgetFontId;
  onChange: (font: WidgetFontId) => void;
  disabled: boolean;
}

/**
 * The list shows every option in its own typeface, so the families are fetched when the list is
 * about to open (not on page load): one `<link>` per font, the same ones the widget would add.
 */
function loadEveryFont(): void {
  for (const id of WIDGET_FONT_IDS) ensureWidgetFontStylesheet(document, id);
}

/** Widget Studio → Appearance → Font: "Match your website" (inherit) or one of eight typefaces. */
export function FontPicker({ value, onChange, disabled }: FontPickerProps) {
  const { t } = useTranslation();
  useWidgetFontStylesheet(value);
  const family = widgetFontFamily(value);
  const stack = widgetFontStack(value);
  const hint =
    value === "website" ? t(`${K}.fontWebsiteHint`) : stack ? t(`${K}.fontGoogleHint`, { font: family ?? "" }) : t(`${K}.fontDefaultHint`);
  return (
    <div className="space-y-2">
      <Field label={t(`${K}.font`)} hint={t(`${K}.fontHint`)}>
        <Select
          value={value}
          disabled={disabled}
          onFocus={loadEveryFont}
          onPointerDown={loadEveryFont}
          onChange={(event) => {
            onChange(toWidgetFontId(event.target.value));
          }}
        >
          {WIDGET_FONT_IDS.map((id) => {
            const optionStack = widgetFontStack(id);
            return (
              <option
                key={id}
                value={id}
                className={cn(optionStack && "font-[family-name:var(--wg-font)]")}
                style={cssVars({ "--wg-font": optionStack ?? undefined })}
              >
                {id === "website" ? t(`${K}.fontWebsite`) : widgetFontFamily(id)}
              </option>
            );
          })}
        </Select>
      </Field>
      <p
        data-testid="font-sample"
        className={cn("rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-fg", stack && "font-[family-name:var(--wg-font)]")}
        style={cssVars({ "--wg-font": stack ?? undefined })}
      >
        {t(`${K}.fontSample`)}
      </p>
      <p className="text-xs text-fg-subtle">{hint}</p>
    </div>
  );
}
